import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { tvLiveRadios, tvPrograms, tvEvents, tvStats, tvPlatforms, type TvRadio, type TvProgram, type TvEvent } from "@/mocks/tvApp";
import { clips, duracionLabels as clipDuracionLabels, formatearNumero as clipFormatearNumero, type Clip } from "@/mocks/clips";
import { useAudioPlayer } from "@/context/AudioContext";
import { useTvPairing } from "@/context/TvPairingContext";
import { radioStations, categoriesWithIcons, type RadioStation } from "@/mocks/radios";
import { programs, type Program } from "@/mocks/programs";
import { adminEvents, type RadioEvent } from "@/mocks/adminEvents";
import { adminContests, type Contest } from "@/mocks/adminContests";
import { historyEntries, type HistoryEntry } from "@/mocks/listenerHistory";
import TvSearchOverlay from "@/pages/tv-app/components/TvSearchOverlay";
import TvCarousel from "@/pages/tv-app/components/TvCarousel";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function TvRadioCard({ radio, index }: { radio: TvRadio; index: number }) {
  const navigate = useNavigate();

  return (
    <a
      href={`/tv-app/player/${radio.id}`}
      onClick={(e) => { e.preventDefault(); navigate(`/tv-app/player/${radio.id}`); }}
      data-tv-focusable="true"
      data-tv-row="radio"
      data-tv-index={index}
      className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-white focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-white/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={radio.image} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>

      {radio.isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
          EN VIVO
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-xl font-bold text-white truncate drop-shadow-md">{radio.name}</h3>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-sm text-white/70">{radio.frequency}</span>
          <span className="text-sm text-white/70">{radio.category}</span>
          {radio.isLive && (
            <span className="text-sm text-white/90 font-semibold flex items-center gap-1">
              <i className="ri-headphone-line"></i>
              {formatCount(radio.listeners)}
            </span>
          )}
        </div>
        {radio.currentProgram && (
          <p className="text-base text-white/90 mt-2 font-semibold truncate flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-accent-400"></span>
            {radio.currentProgram}
          </p>
        )}
      </div>
    </a>
  );
}

function TvProgramCard({ program, index }: { program: TvProgram; index: number }) {
  const navigate = useNavigate();

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/program/${program.name.toLowerCase().replace(/\s+/g, '-')}`)}
      data-tv-focusable="true"
      data-tv-row="program"
      data-tv-index={index}
      className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-emerald-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-emerald-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={program.image} alt={program.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>

      {program.isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          AL AIRE
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{program.name}</h3>
        <p className="text-sm text-white/70 mt-1.5">{program.host}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs text-white/50">{program.radioName}</span>
          <span className="text-xs text-white/50">{program.time}</span>
        </div>
      </div>
    </div>
  );
}

function TvEventCard({ event, index }: { event: TvEvent; index: number }) {
  const navigate = useNavigate();
  const date = new Date(event.date);
  const formattedDate = date.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/event/${event.id}`)}
      data-tv-focusable="true"
      data-tv-row="event"
      data-tv-index={index}
      className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-amber-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-amber-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={event.image} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>

      <div className="absolute top-4 right-4">
        <span className="px-3 py-1 rounded-full bg-amber-500/90 text-white text-[10px] font-bold">{event.type}</span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{event.title}</h3>
        <p className="text-sm text-white/70 mt-1.5">{event.radioName}</p>
        <p className="text-xs text-white/50 mt-1.5 flex items-center gap-1.5">
          <i className="ri-calendar-line text-[11px]"></i>
          {formattedDate}
        </p>
      </div>
    </div>
  );
}

function TvClipCard({ clip, index }: { clip: Clip; index: number }) {
  const navigate = useNavigate();
  const durInfo = clipDuracionLabels[clip.duracion];

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/video/${clip.id}`)}
      data-tv-focusable="true"
      data-tv-row="clip"
      data-tv-index={index}
      className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-accent-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-accent-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={clip.imagenUrl} alt={clip.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
          <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
        </div>
      </div>

      <div className="absolute top-4 left-4 flex items-center gap-2">
        {clip.esTendencia && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500/90 text-white text-xs font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            TENDENCIA
          </span>
        )}
        {durInfo && (
          <span className="px-2 py-0.5 rounded-md bg-white/20 backdrop-blur-sm text-white text-xs font-bold">{durInfo.label}</span>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{clip.titulo}</h3>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-sm text-white/70 flex items-center gap-1">
            <img src={clip.radioLogo} alt="" className="w-5 h-5 rounded-full object-cover" />
            {clip.radioNombre}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-sm text-white/60 flex items-center gap-1">
            <i className="ri-eye-line text-xs"></i>
            {clipFormatearNumero(clip.vistas)}
          </span>
          <span className="text-sm text-white/60 flex items-center gap-1">
            <i className="ri-share-forward-line text-xs"></i>
            {clipFormatearNumero(clip.compartidos)}
          </span>
        </div>
      </div>
    </div>
  );
}

function TvContestCard({ contest, index }: { contest: Contest; index: number }) {
  const navigate = useNavigate();

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/contest/${contest.id}`)}
      data-tv-focusable="true"
      data-tv-row="contest"
      data-tv-index={index}
      className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-violet-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-violet-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 via-foreground-950/80 to-foreground-950/90"></div>

      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/90 text-white text-xs font-bold">
          <i className="ri-trophy-line text-xs"></i>
          {contest.status === "activo" ? "ACTIVO" : contest.status === "proximamente" ? "PRÓXIMO" : "FINALIZADO"}
        </span>
      </div>

      <div className="absolute top-4 right-4">
        <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs font-bold">
          {contest.prize.length > 30 ? contest.prize.slice(0, 30) + "…" : contest.prize}
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{contest.title}</h3>
        <p className="text-sm text-white/60 mt-1.5 truncate">{contest.description.split('.')[0]}</p>
        <div className="flex items-center gap-4 mt-2">
          <span className="text-sm text-white/70 flex items-center gap-1">
            <i className="ri-user-line text-xs"></i>
            {formatCount(contest.participants)}
          </span>
          <span className="text-sm text-white/70 flex items-center gap-1">
            <i className="ri-share-forward-line text-xs"></i>
            {formatCount(contest.shares)}
          </span>
        </div>
      </div>
    </div>
  );
}

function TvContinueListeningCard({ entry, index, onPlay }: { entry: HistoryEntry; index: number; onPlay: (stationId: number) => void }) {
  const radio = radioStations.find((r) => entry.title.includes(r.name));
  const timeLabel = (() => {
    const diff = Date.now() - new Date(entry.timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs}h`;
    return `Hace ${Math.floor(hrs / 24)}d`;
  })();

  return (
    <div
      tabIndex={0}
      data-tv-focusable="true"
      data-tv-row="continue"
      data-tv-index={index}
      onClick={() => radio && onPlay(radio.id)}
      className="relative flex-shrink-0 w-[280px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-accent-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-accent-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      {entry.image ? (
        <img src={entry.image} alt={entry.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-accent-400/10 to-foreground-950/50"></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-14 h-14 rounded-full bg-accent-400/90 flex items-center justify-center">
          <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
        </div>
      </div>

      <div className="absolute top-4 right-4">
        <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold">{timeLabel}</span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-base font-bold text-white truncate drop-shadow-md">{entry.title}</h3>
        <p className="text-sm text-white/60 mt-1 truncate">{entry.subtitle}</p>
        <p className="text-xs text-accent-400 mt-1.5 font-semibold flex items-center gap-1">
          <i className="ri-play-circle-line"></i>
          Continuar escuchando
        </p>
      </div>
    </div>
  );
}

function TvContinueWatchingCard({ entry, index, onNavigate }: { entry: HistoryEntry; index: number; onNavigate: (path: string) => void }) {
  const timeLabel = (() => {
    const diff = Date.now() - new Date(entry.timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs}h`;
    return `Hace ${Math.floor(hrs / 24)}d`;
  })();

  const isPodcast = entry.type === 'podcast';

  return (
    <div
      tabIndex={0}
      data-tv-focusable="true"
      data-tv-row="continue-watch"
      data-tv-index={index}
      onClick={() => entry.targetPath && onNavigate(entry.targetPath)}
      className="relative flex-shrink-0 w-[280px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-amber-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-amber-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      {entry.image ? (
        <img src={entry.image} alt={entry.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-amber-400/10 to-foreground-950/50"></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isPodcast ? 'bg-violet-400/90' : 'bg-amber-400/90'}`}>
          <i className={`text-white text-2xl ml-0.5 ${isPodcast ? 'ri-headphone-fill' : 'ri-play-fill'}`}></i>
        </div>
      </div>

      <div className="absolute top-4 right-4">
        <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold">{timeLabel}</span>
      </div>

      {entry.metadata && (
        <div className="absolute top-4 left-4">
          <span className={`px-2 py-0.5 rounded-md text-white text-[10px] font-bold ${isPodcast ? 'bg-violet-500/80' : 'bg-amber-500/80'}`}>
            {isPodcast ? 'Podcast' : 'Video'}
          </span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-base font-bold text-white truncate drop-shadow-md">{entry.title}</h3>
        <p className="text-sm text-white/60 mt-1 truncate">{entry.subtitle}</p>
        <p className="text-xs text-amber-400 mt-1.5 font-semibold flex items-center gap-1">
          <i className={isPodcast ? 'ri-headphone-line' : 'ri-play-circle-line'}></i>
          {entry.metadata || 'Continuar viendo'}
        </p>
      </div>
    </div>
  );
}

function TvSharedRadioCard({ radio }: { radio: RadioStation }) {
  const navigate = useNavigate();

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/radio/${radio.id}`)}
      data-tv-focusable="true"
      data-tv-row="radio"
      className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-white focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-white/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
      {radio.isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
          EN VIVO
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-xl font-bold text-white truncate drop-shadow-md">{radio.name}</h3>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-sm text-white/70">{radio.frequency}</span>
          <span className="text-sm text-white/70">{radio.category}</span>
          {radio.isLive && (
            <span className="text-sm text-white/90 font-semibold flex items-center gap-1">
              <i className="ri-headphone-line"></i>
              {formatCount(radio.listeners)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function TvSharedProgramCard({ program }: { program: Program }) {
  const navigate = useNavigate();

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/program/${program.slug}`)}
      data-tv-focusable="true"
      data-tv-row="program"
      className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-emerald-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-emerald-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={program.coverUrl} alt={program.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
      {program.isActive && (
        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-xs font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          ACTIVO
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{program.name}</h3>
        <p className="text-sm text-white/70 mt-1.5">{program.hosts[0]?.name || 'Varios'} · {program.radioName}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs text-white/50">{program.schedule}</span>
          <span className="text-xs text-white/50">{program.category}</span>
        </div>
      </div>
    </div>
  );
}

function TvSharedEventCard({ event }: { event: RadioEvent }) {
  const navigate = useNavigate();
  const typeLabel = event.type === 'concierto' ? 'Concierto' : event.type === 'transmision_especial' ? 'Transmisión' : event.type;
  const date = new Date(event.date);
  const formattedDate = date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });

  return (
    <div
      tabIndex={0}
      onClick={() => navigate(`/tv-app/event/${event.id}`)}
      data-tv-focusable="true"
      data-tv-row="event"
      className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-amber-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-amber-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]"
    >
      <img src={event.image} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
      <div className="absolute top-4 right-4">
        <span className="px-3 py-1 rounded-full bg-amber-500/90 text-white text-xs font-bold">{typeLabel}</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{event.title}</h3>
        <p className="text-sm text-white/70 mt-1.5">{formattedDate}</p>
      </div>
    </div>
  );
}

export default function TvAppPage() {
  const navigate = useNavigate();
  const { play, toggle, stop, currentStation, isPlaying: isAudioPlaying } = useAudioPlayer();
  const { session } = useTvPairing();
  const [activeNav, setActiveNav] = useState("home");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [focusedNavIdx, setFocusedNavIdx] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // ── Datos para Home TV: 9 filas ──
  const continueListening = historyEntries.filter(e => e.type === 'radio').slice(0, 8);
  const continueWatching = historyEntries.filter(e => e.type === 'video' || e.type === 'podcast' || e.type === 'programa').slice(0, 8);
  const liveNow = radioStations.filter(r => r.isLive).slice(0, 10);
  const featuredRadios = radioStations.filter(r => r.isTopRadio).slice(0, 10);
  const activePrograms = programs.filter(p => p.isActive).slice(0, 10);
  const recentClips = clips.slice(0, 10);
  const upcomingEvents = adminEvents.filter(e => e.status === 'publicado').slice(0, 10);
  const activeContests = adminContests.filter(c => c.status === 'activo').slice(0, 10);

  // ── Datos heredados para secciones de navegación ──
  const liveRadios = tvLiveRadios.filter((r) => r.isLive);
  const livePrograms = tvPrograms.filter((p) => p.isLive);
  const featuredEvents = tvEvents;

  const navItems = [
    { id: "home", label: "Inicio", icon: "ri-home-5-line" },
    { id: "live", label: "En Vivo", icon: "ri-live-line" },
    { id: "radios", label: "Radios", icon: "ri-radio-line" },
    { id: "programs", label: "Programas", icon: "ri-mic-line" },
    { id: "videos", label: "Videos", icon: "ri-film-line" },
    { id: "podcasts", label: "Podcasts", icon: "ri-headphone-line" },
    { id: "events", label: "Eventos", icon: "ri-calendar-event-line" },
  ];

  const handleNavKeyDown = useCallback((e: React.KeyboardEvent, idx: number) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(idx + 1, navItems.length - 1);
      setFocusedNavIdx(next);
      (document.querySelector(`[data-tv-nav="${next}"]`) as HTMLElement)?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(idx - 1, 0);
      setFocusedNavIdx(prev);
      (document.querySelector(`[data-tv-nav="${prev}"]`) as HTMLElement)?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      setActiveNav(navItems[idx].id);
      setSelectedCategory(null);
    }
  }, []);

  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Backspace") {
        if (selectedCategory !== null) {
          setSelectedCategory(null);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, [selectedCategory]);

  // ── Navegación secundaria: guardar estado para restaurar al volver ──
  const saveStateAndNavigate = useCallback((path: string) => {
    if (mainContentRef.current) {
      sessionStorage.setItem('tv_scroll_y', String(mainContentRef.current.scrollTop));
    }
    sessionStorage.setItem('tv_active_nav', activeNav);
    if (selectedCategory !== null) {
      sessionStorage.setItem('tv_selected_category', String(selectedCategory));
    } else {
      sessionStorage.removeItem('tv_selected_category');
    }
    // Guardar foco activo
    const focusedEl = document.activeElement as HTMLElement;
    if (focusedEl) {
      const row = focusedEl.getAttribute('data-tv-row');
      const idx = focusedEl.getAttribute('data-tv-index');
      if (row) sessionStorage.setItem('tv_focus_row', row);
      if (idx) sessionStorage.setItem('tv_focus_index', idx);
    }
    navigate(path);
  }, [activeNav, selectedCategory, navigate]);

  // Restaurar estado al montar
  useEffect(() => {
    const savedNav = sessionStorage.getItem('tv_active_nav');
    const savedCat = sessionStorage.getItem('tv_selected_category');
    const savedScroll = sessionStorage.getItem('tv_scroll_y');

    if (savedNav && navItems.some(n => n.id === savedNav)) {
      setActiveNav(savedNav);
    }
    if (savedCat) {
      setSelectedCategory(Number(savedCat));
    }
    if (savedScroll && mainContentRef.current) {
      requestAnimationFrame(() => {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = Number(savedScroll);
          // Restaurar foco
          const savedRow = sessionStorage.getItem('tv_focus_row');
          const savedIdx = sessionStorage.getItem('tv_focus_index');
          if (savedRow && savedIdx) {
            const el = document.querySelector(`[data-tv-row="${savedRow}"][data-tv-index="${savedIdx}"]`) as HTMLElement;
            if (el) {
              setTimeout(() => el.focus(), 100);
            }
          }
        }
      });
    }
  }, []);

  // Guardar scroll en tiempo real
  const handleMainScroll = useCallback(() => {
    if (mainContentRef.current) {
      sessionStorage.setItem('tv_scroll_y', String(mainContentRef.current.scrollTop));
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none">
      <div className="flex h-screen">
        {/* Sidebar — TV Navigation with remote focus */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-[#0f0f15] border-r border-white/5 h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
                <i className="ri-radio-line text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-base font-bold text-white">RFW TV</h1>
                <p className="text-[10px] text-white/40">RadioFrecuenciaWeb</p>
              </div>
            </div>
          </div>

          {/* Nav items with remote focus */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest px-2 mb-3">Navegación</p>
            {navItems.map((item, idx) => (
              <button
                key={item.id}
                data-tv-nav={idx}
                tabIndex={0}
                onClick={() => { setActiveNav(item.id); setSelectedCategory(null); }}
                onKeyDown={(e) => handleNavKeyDown(e, idx)}
                onFocus={() => setFocusedNavIdx(idx)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15] ${
                  activeNav === item.id && selectedCategory === null
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                }`}
              >
                <i className={`${item.icon} text-lg`}></i>
                <span>{item.label}</span>
              </button>
            ))}

            {/* Categories */}
            <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest px-2 mt-6 mb-3">Categorías</p>
            {categoriesWithIcons.slice(0, 8).map((cat, idx) => (
              <button
                key={cat.name}
                tabIndex={0}
                onClick={() => { setSelectedCategory(idx); setActiveNav("home"); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15] ${
                  selectedCategory === idx
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <i className={`${cat.icon} text-white/80`}></i>
                </div>
                <div className="text-left">
                  <p>{cat.name}</p>
                </div>
              </button>
            ))}
          </nav>

          {/* Action buttons */}
          <div className="p-4 space-y-2 border-t border-white/5">
            {/* Pairing Status */}
            <div className="mb-3">
              {session.state === 'CONNECTED_MOCK' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                    <span className="text-xs text-emerald-400 font-semibold truncate">{session.deviceName}</span>
                  </div>
                  <button
                    onClick={() => navigate("/tv-app/remote")}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15]"
                    tabIndex={0}
                  >
                    <i className="ri-remote-control-line text-lg"></i>
                    Control Remoto
                  </button>
                  <button
                    onClick={() => navigate("/tv-app/qr")}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white/50 hover:text-white/80 hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15]"
                    tabIndex={0}
                  >
                    <i className="ri-qr-code-line text-lg"></i>
                    Gestionar Vinculación
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate("/tv-app/qr")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15]"
                  tabIndex={0}
                >
                  <i className="ri-qr-code-line text-lg"></i>
                  Vincular Cuenta
                </button>
              )}
            </div>
            {/* Platforms */}
            <div className="flex gap-1.5 flex-wrap pt-2">
              {tvPlatforms.map((p) => (
                <span key={p.name} className={`flex items-center gap-1 px-2 py-1 rounded-full ${p.bg} text-[10px] font-medium ${p.color}`}>
                  <i className={`${p.icon} text-[10px]`}></i>
                  {p.name}
                </span>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main ref={mainContentRef} onScroll={handleMainScroll} data-tv-main-content className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Top Bar */}
          <header className="sticky top-0 z-20 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
            <div className="flex items-center justify-between px-6 lg:px-10 py-5">
              <div className="flex lg:hidden items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
                  <i className="ri-radio-line text-white text-sm"></i>
                </div>
                <h1 className="text-base font-bold text-white">RFW TV</h1>
              </div>

              <div className="flex items-center gap-6">
                {/* Search button — always visible */}
                <button
                  tabIndex={0}
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/60 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]"
                >
                  <i className="ri-search-line"></i>
                  <span className="hidden sm:inline">Buscar</span>
                  <span className="hidden sm:inline text-white/20 text-xs ml-1">Ctrl+K</span>
                </button>

                <div className="hidden sm:flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-base font-bold text-white">{tvStats.totalStations}</p>
                    <p className="text-[10px] text-white/40">Emisoras</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                      <p className="text-base font-bold text-white">{tvStats.liveNow}</p>
                    </div>
                    <p className="text-[10px] text-white/40">En Vivo</p>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-bold text-white">{formatCount(tvStats.totalListeners)}</p>
                    <p className="text-[10px] text-white/40">Oyentes</p>
                  </div>
                </div>

                {/* Mobile nav pills */}
                <div className="flex lg:hidden gap-1">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveNav(item.id)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        activeNav === item.id ? "bg-white/15 text-white" : "text-white/50"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                {/* Desktop QR link */}
                <button
                  tabIndex={0}
                  onClick={() => navigate("/tv-app/qr")}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowpace transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]"
                >
                  <i className="ri-qr-code-line"></i>
                  Vincular
                </button>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="px-6 lg:px-10 py-8 pb-24 space-y-10">
            {/* ━━━ HOME TV — 9 Filas ━━━ */}
            {activeNav === "home" && !selectedCategory && (
              <>
                {/* ── 1. Continue Listening ── */}
                {continueListening.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-accent-400/10 flex items-center justify-center">
                            <i className="ri-history-line text-accent-400 text-sm"></i>
                          </div>
                          <h2 className="text-2xl font-bold text-white">Continuar Escuchando</h2>
                        </div>
                        <p className="text-sm text-white/40 mt-1">Retomá donde lo dejaste</p>
                      </div>
                    </div>
                    <TvCarousel>
                      {continueListening.map((entry, idx) => (
                        <div key={entry.id} style={{ scrollSnapAlign: "start" }}>
                          <TvContinueListeningCard entry={entry} index={idx} onPlay={(id) => { const s = radioStations.find(r => r.id === id); if (s) play(s); }} />
                        </div>
                      ))}
                    </TvCarousel>
                  </section>
                )}

                {/* ── 2. Continue Watching ── */}
                {continueWatching.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <i className="ri-play-circle-line text-amber-400 text-sm"></i>
                          </div>
                          <h2 className="text-2xl font-bold text-white">Continuar Viendo</h2>
                        </div>
                        <p className="text-sm text-white/40 mt-1">Retomá videos y podcasts donde los dejaste</p>
                      </div>
                    </div>
                    <TvCarousel>
                      {continueWatching.map((entry, idx) => (
                        <div key={entry.id} style={{ scrollSnapAlign: "start" }}>
                          <TvContinueWatchingCard entry={entry} index={idx} onNavigate={saveStateAndNavigate} />
                        </div>
                      ))}
                    </TvCarousel>
                  </section>
                )}

                {/* ── 3. Live Now ── */}
                <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                        </div>
                        <h2 className="text-2xl font-bold text-white">En Vivo Ahora</h2>
                        <span className="text-sm text-white/40">{liveNow.length} emisoras</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("live")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todas</button>
                  </div>
                  <TvCarousel>
                    {liveNow.map((radio) => (
                      <div key={radio.id} style={{ scrollSnapAlign: "start" }} className="cursor-pointer">
                        <TvSharedRadioCard radio={radio} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>

                {/* ── 4. Featured Radios ── */}
                <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Radios Destacadas</h2>
                        <span className="text-sm text-white/40">{featuredRadios.length}</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("radios")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todas</button>
                  </div>
                  <TvCarousel>
                    {featuredRadios.map((radio) => (
                      <div key={radio.id} style={{ scrollSnapAlign: "start" }} className="cursor-pointer">
                        <TvSharedRadioCard radio={radio} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>

                {/* ── 5. Featured Programs ── */}
                {activePrograms.length > 0 && (
                  <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <i className="ri-mic-line text-emerald-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Programas</h2>
                        <span className="text-sm text-white/40">{activePrograms.length} activos</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("programs")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todos</button>
                  </div>
                  <TvCarousel>
                    {activePrograms.map((prog) => (
                      <div key={prog.id} style={{ scrollSnapAlign: "start" }}>
                        <TvSharedProgramCard program={prog} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>
                )}

                {/* ── 6. Videos ── */}
                <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent-400/10 flex items-center justify-center">
                          <i className="ri-film-line text-accent-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Videos</h2>
                        <span className="text-sm text-white/40">{recentClips.length} clips</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("videos")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todos</button>
                  </div>
                  <TvCarousel>
                    {recentClips.map((clip, idx) => (
                      <div key={clip.id} style={{ scrollSnapAlign: "start" }}>
                        <TvClipCard clip={clip} index={idx} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>

                {/* ── 7. Podcasts ── */}
                <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                          <i className="ri-headphone-line text-violet-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Podcasts</h2>
                        <span className="text-sm text-white/40">{programs.length} disponibles</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("podcasts")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todos</button>
                  </div>
                  <TvCarousel>
                    {programs.slice(0, 8).map((prog) => (
                      <div key={prog.id} style={{ scrollSnapAlign: "start" }} onClick={() => navigate(`/tv-app/podcast/${prog.id}`)} className="cursor-pointer">
                        <TvSharedProgramCard program={prog} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>

                {/* ── 8. Events ── */}
                {upcomingEvents.length > 0 && (
                  <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <i className="ri-calendar-event-line text-amber-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Eventos</h2>
                        <span className="text-sm text-white/40">{upcomingEvents.length} próximos</span>
                      </div>
                    </div>
                    <button tabIndex={0} onClick={() => setActiveNav("events")} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">Ver todos</button>
                  </div>
                  <TvCarousel>
                    {upcomingEvents.map((evt) => (
                      <div key={evt.id} style={{ scrollSnapAlign: "start" }}>
                        <TvSharedEventCard event={evt} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>
                )}

                {/* ── 9. Contests ── */}
                {activeContests.length > 0 && (
                  <section>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                          <i className="ri-trophy-line text-violet-400 text-sm"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Concursos</h2>
                        <span className="text-sm text-white/40">{activeContests.length} activos</span>
                      </div>
                    </div>
                  </div>
                  <TvCarousel>
                    {activeContests.map((contest, idx) => (
                      <div key={contest.id} style={{ scrollSnapAlign: "start" }}>
                        <TvContestCard contest={contest} index={idx} />
                      </div>
                    ))}
                  </TvCarousel>
                </section>
                )}

                {/* ── Categories quick access ── */}
                <section>
                  <h2 className="text-2xl font-bold text-white mb-5">Categorías</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                    {categoriesWithIcons.slice(0, 8).map((cat) => (
                      <button
                        key={cat.name}
                        tabIndex={0}
                        onClick={() => setSelectedCategory(categoriesWithIcons.indexOf(cat))}
                        className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all cursor-pointer outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f] text-center"
                      >
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                          <i className={`${cat.icon} text-white/80 text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">{cat.name}</p>
                          <p className="text-[10px] text-white/40">{radioStations.filter(r => r.category === cat.name).length}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>

                {/* ── Download App CTA ── */}
                <div className="p-8 rounded-3xl bg-gradient-to-br from-rose-500/10 via-orange-500/5 to-amber-500/10 border border-white/10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-4">
                    <i className="ri-smartphone-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">¿Preferís el celu?</h3>
                  <p className="text-sm text-white/60 mb-5 max-w-md mx-auto">La app móvil de RFW tiene chat en vivo, comunidad, gamificación y mucho más.</p>
                  <button tabIndex={0} onClick={() => navigate("/descargar")} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0a0a0f] font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:bg-white/90 outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">
                    <i className="ri-download-line"></i>
                    Descargar App Móvil
                  </button>
                </div>
              </>
            )}

            {/* Category Filtered View */}
            {selectedCategory !== null && (
              <section>
                <div className="flex items-center gap-4 mb-6">
                  <button tabIndex={0} onClick={() => setSelectedCategory(null)} className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]">
                    <i className="ri-arrow-left-line text-white"></i>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{categoriesWithIcons[selectedCategory]?.name}</h2>
                    <p className="text-sm text-white/40">{radioStations.filter(r => r.category === categoriesWithIcons[selectedCategory]?.name).length} emisoras</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {radioStations.filter(r => r.category === categoriesWithIcons[selectedCategory]?.name).map((radio) => (
                    <div key={radio.id} tabIndex={0} onClick={() => navigate(`/tv-app/radio/${radio.id}`)} className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-white focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-white/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]">
                      <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                      {radio.isLive && (<div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>EN VIVO</div>)}
                      {currentStation?.id === radio.id && isAudioPlaying && (<div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-400/90 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>SONANDO</div>)}
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="text-lg font-bold text-white truncate drop-shadow-md">{radio.name}</h3>
                        <p className="text-sm text-white/70 mt-1.5">{radio.frequency} · {radio.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* LIVE Section */}
            {activeNav === "live" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">En Vivo Ahora</h2><p className="text-sm text-white/40">{liveRadios.length} emisoras · {formatCount(tvStats.totalListeners)} oyentes</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {liveRadios.map((radio) => (
                    <a key={radio.id} href={`/tv-app/player/${radio.id}`} onClick={(e) => { e.preventDefault(); navigate(`/tv-app/player/${radio.id}`); }} tabIndex={0} className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-white focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-white/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]">
                      <img src={radio.image} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>EN VIVO</div>
                      <div className="absolute bottom-0 left-0 right-0 p-5"><h3 className="text-lg font-bold text-white truncate drop-shadow-md">{radio.name}</h3><p className="text-sm text-white/70 mt-1.5">{radio.frequency} · {radio.category}</p></div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* PROGRAMS Section */}
            {activeNav === "programs" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">Programación</h2><p className="text-sm text-white/40">{tvStats.totalPrograms} programas disponibles</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {tvPrograms.map((prog) => (<TvProgramCard key={prog.id} program={prog} index={prog.id} />))}
                </div>
              </section>
            )}

            {/* EVENTS Section */}
            {activeNav === "events" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">Próximos Eventos</h2><p className="text-sm text-white/40">{featuredEvents.length} eventos</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {featuredEvents.map((evt, idx) => (<TvEventCard key={evt.id} event={evt} index={idx} />))}
                </div>
              </section>
            )}

            {/* RADIOS Section */}
            {activeNav === "radios" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">Todas las Emisoras</h2><p className="text-sm text-white/40">{radioStations.length} emisoras</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {radioStations.map((radio) => (
                    <div key={radio.id} tabIndex={0} onClick={() => navigate(`/tv-app/radio/${radio.id}`)} className={`relative w-full aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-white focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-white/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f] ${!radio.isLive ? 'opacity-70' : ''}`}>
                      <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                      {radio.isLive ? (<div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/90 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>EN VIVO</div>) : (<div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground-500/60 text-white text-xs font-bold"><i className="ri-pause-circle-line"></i>OFFLINE</div>)}
                      {currentStation?.id === radio.id && isAudioPlaying && (<div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-400/90 text-white text-xs font-bold"><span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>SONANDO</div>)}
                      <div className="absolute bottom-0 left-0 right-0 p-5"><h3 className="text-lg font-bold text-white truncate drop-shadow-md">{radio.name}</h3><p className="text-sm text-white/70 mt-1.5">{radio.frequency} · {radio.category}</p><p className="text-xs text-white/50 mt-1">{radio.city}, {radio.country}</p></div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* VIDEOS Section */}
            {activeNav === "videos" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">Videos y Clips</h2><p className="text-sm text-white/40">{clips.length} clips</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {clips.map((clip, idx) => (<TvClipCard key={clip.id} clip={clip} index={idx} />))}
                </div>
              </section>
            )}

            {/* PODCASTS Section */}
            {activeNav === "podcasts" && (
              <section>
                <div className="mb-6"><h2 className="text-2xl font-bold text-white mb-1">Podcasts</h2><p className="text-sm text-white/40">{programs.length} disponibles</p></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {programs.map((prog) => (
                    <div key={prog.id} onClick={() => navigate(`/tv-app/podcast/${prog.id}`)} className="cursor-pointer">
                      <TvSharedProgramCard program={prog} />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Download App CTA */}
            <div className="p-8 rounded-3xl bg-gradient-to-br from-rose-500/20 via-orange-500/10 to-amber-500/20 border border-white/10 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-4"><i className="ri-smartphone-line text-white text-2xl"></i></div>
              <h3 className="text-xl font-bold text-white mb-2">¿Preferís el celu?</h3>
              <p className="text-sm text-white/60 mb-5 max-w-md mx-auto">La app móvil de RFW tiene chat en vivo, comunidad, gamificación y mucho más.</p>
              <button tabIndex={0} onClick={() => navigate("/descargar")} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-[#0a0a0f] font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:bg-white/90 outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0a0a0f]"><i className="ri-download-line"></i>Descargar App Móvil</button>
            </div>
          </div>
        </main>
      </div>
      <TvSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} saveStateAndNavigate={saveStateAndNavigate} />

      {/* ── Mini Player TV ── */}
      {currentStation && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0f0f15]/95 backdrop-blur-xl border-t border-white/5">
          <div className="flex items-center gap-4 px-6 py-4 max-w-full">
            {/* Station logo + info */}
            <button
              tabIndex={0}
              onClick={() => navigate(`/tv-app/player/${currentStation.id}`)}
              className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15] rounded-xl p-1 -m-1 transition-all"
            >
              <div className="relative flex-shrink-0">
                <div className={`w-12 h-12 rounded-xl overflow-hidden ring-2 ${isAudioPlaying ? 'ring-accent-400' : 'ring-white/10'}`}>
                  <img src={currentStation.logoUrl} alt={currentStation.name} className="w-full h-full object-cover" />
                </div>
                {isAudioPlaying && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-end gap-0.5 h-3">
                    {[3, 5, 4, 6, 3].map((h, i) => (
                      <div key={i} className="w-1 rounded-full bg-accent-400" style={{ height: `${h * 2}px`, animation: `tv-eq-mini 0.6s ease-in-out infinite`, animationDelay: `${i * 0.12}s` }}></div>
                    ))}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white truncate">{currentStation.name}</p>
                <p className="text-xs text-white/50 truncate">{currentStation.frequency} · {isAudioPlaying ? 'Reproduciendo' : 'Pausado'}</p>
              </div>
            </button>

            {/* Play/Pause */}
            <button
              tabIndex={0}
              onClick={() => toggle()}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all cursor-pointer outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15] ${
                isAudioPlaying ? 'bg-white text-[#0a0a0f] hover:bg-white/90' : 'bg-white/10 hover:bg-white/15 text-white'
              }`}
            >
              <i className={`text-lg ${isAudioPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
            </button>

            {/* Stop */}
            <button
              tabIndex={0}
              onClick={() => stop()}
              className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-all cursor-pointer outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15]"
            >
              <i className="ri-stop-fill text-lg"></i>
            </button>

            {/* Full player link */}
            <button
              tabIndex={0}
              onClick={() => navigate(`/tv-app/player/${currentStation.id}`)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0f0f15]"
            >
              <i className="ri-fullscreen-line"></i>
              <span className="hidden sm:inline">Pantalla completa</span>
            </button>
          </div>
        </div>
      )}

      {/* Equalizer mini keyframes */}
      <style>{`
        @keyframes tv-eq-mini {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}